<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InstagramAutoReplyAPI from '../../../../api/instagramAutoReply';

const props = defineProps({
  rule: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(['close', 'saved']);

const { t } = useI18n();
const allInboxes = useMapGetter('inboxes/getInboxes');

const isEditing = computed(() => !!props.rule && !!props.rule.id);
const loading = ref(false);

const keywordsList = ref([]);
const keywordInput = ref('');
const keywordInputRef = ref(null);
const buttonsList = ref([]);

const formData = ref({
  name: '',
  inbox_id: '',
  trigger_keywords: '',
  trigger_regex: '',
  reply_text: '',
  dm_text: '',
  post_id: '',
  priority: 0,
  is_active: true,
  is_default: false,
});

const inboxes = computed(() => {
  return (allInboxes.value || []).filter(
    inbox => inbox.channel_type === 'Channel::Instagram'
  );
});

onMounted(() => {
  if (props.rule) {
    formData.value = {
      name: props.rule.name || '',
      inbox_id: props.rule.inbox_id || '',
      trigger_keywords: props.rule.trigger_keywords || '',
      trigger_regex: props.rule.trigger_regex || '',
      reply_text: props.rule.reply_text || '',
      dm_text: props.rule.dm_text || '',
      post_id: props.rule.post_id || '',
      priority: props.rule.priority || 0,
      is_active: props.rule.is_active ?? true,
      is_default: props.rule.is_default ?? false,
    };

    if (props.rule.trigger_keywords) {
      keywordsList.value = props.rule.trigger_keywords
        .split(/[\n,]+/)
        .map(k => k.trim())
        .filter(Boolean);
    }

    if (Array.isArray(props.rule.buttons)) {
      buttonsList.value = props.rule.buttons.map(btn => ({
        title: btn.title || '',
        type: btn.type || 'web_url',
        url: btn.url || '',
        payload: btn.payload || '',
      }));
    }
  }
});

const addKeyword = (value = null) => {
  const raw = value !== null ? value : keywordInput.value;
  if (!raw || typeof raw !== 'string') return;

  const parts = raw.split(/[\n,]+/).map(p => p.trim()).filter(Boolean);
  parts.forEach(p => {
    if (!keywordsList.value.includes(p)) {
      keywordsList.value.push(p);
    }
  });
  keywordInput.value = '';
};

const removeKeyword = index => {
  keywordsList.value.splice(index, 1);
};

const handleKeywordKeydown = event => {
  if (event.key === 'Enter' || event.key === ',') {
    event.preventDefault();
    addKeyword();
  } else if (
    event.key === 'Backspace' &&
    !keywordInput.value &&
    keywordsList.value.length > 0
  ) {
    keywordsList.value.pop();
  }
};

const handleKeywordPaste = event => {
  event.preventDefault();
  const pasteData = event.clipboardData?.getData('text') || '';
  addKeyword(pasteData);
};

const addButton = () => {
  if (buttonsList.value.length >= 3) {
    useAlert(t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.MAX_ERROR'));
    return;
  }
  buttonsList.value.push({
    title: '',
    type: 'web_url',
    url: '',
    payload: '',
  });
};

const removeButton = index => {
  buttonsList.value.splice(index, 1);
};

const closeModal = () => {
  emit('close');
};

const handleSubmit = async () => {
  if (!formData.value.name.trim()) {
    useAlert(t('INSTAGRAM_AUTO_REPLY.FORM.NAME.ERROR'));
    return;
  }
  if (!formData.value.reply_text.trim()) {
    useAlert(t('INSTAGRAM_AUTO_REPLY.FORM.REPLY_TEXT.ERROR'));
    return;
  }

  loading.value = true;
  try {
    // If there is pending input in keywordInput, add it before submitting
    if (keywordInput.value.trim()) {
      addKeyword();
    }

    const cleanedButtons = buttonsList.value
      .filter(b => b.title && b.title.trim())
      .map(b => ({
        title: b.title.trim(),
        type: b.type || 'web_url',
        url: b.type === 'web_url' ? (b.url || '').trim() : null,
        payload: b.type === 'postback' ? (b.payload || b.title).trim() : null,
      }));

    const payload = {
      name: formData.value.name.trim(),
      inbox_id: formData.value.inbox_id || null,
      trigger_keywords: keywordsList.value.join(', ') || null,
      trigger_regex: formData.value.trigger_regex.trim() || null,
      reply_text: formData.value.reply_text.trim(),
      dm_text: formData.value.dm_text.trim() || null,
      buttons: cleanedButtons,
      post_id: formData.value.post_id.trim() || null,
      priority: parseInt(formData.value.priority, 10) || 0,
      is_active: formData.value.is_active,
      is_default: formData.value.is_default,
    };

    if (isEditing.value) {
      await InstagramAutoReplyAPI.update(props.rule.id, {
        instagram_auto_reply: payload,
      });
      useAlert(t('INSTAGRAM_AUTO_REPLY.EDIT.SUCCESS_MESSAGE'));
    } else {
      await InstagramAutoReplyAPI.create({
        instagram_auto_reply: payload,
      });
      useAlert(t('INSTAGRAM_AUTO_REPLY.ADD.SUCCESS_MESSAGE'));
    }
    emit('saved');
    closeModal();
  } catch (error) {
    const errorMsg =
      error.response?.data?.errors?.join(', ') ||
      t('INSTAGRAM_AUTO_REPLY.ERROR_MESSAGE');
    useAlert(errorMsg);
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto max-h-[85vh] overflow-y-auto px-6 py-4">
    <woot-modal-header
      :header-title="
        isEditing
          ? $t('INSTAGRAM_AUTO_REPLY.EDIT.TITLE')
          : $t('INSTAGRAM_AUTO_REPLY.ADD.TITLE')
      "
      :header-content="
        isEditing
          ? $t('INSTAGRAM_AUTO_REPLY.EDIT.DESC')
          : $t('INSTAGRAM_AUTO_REPLY.ADD.DESC')
      "
    />

    <form class="flex flex-col w-full gap-4 mt-3" @submit.prevent="handleSubmit">
      <!-- Rule Name -->
      <div class="w-full">
        <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.NAME.LABEL') }} *
        </label>
        <input
          v-model="formData.name"
          type="text"
          required
          class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          :placeholder="$t('INSTAGRAM_AUTO_REPLY.FORM.NAME.PLACEHOLDER')"
        />
      </div>

      <!-- Inbox Select (Optional) -->
      <div v-if="inboxes.length > 0" class="w-full">
        <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.INBOX.LABEL') }}
        </label>
        <select
          v-model="formData.inbox_id"
          class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
        >
          <option value="">
            {{ $t('INSTAGRAM_AUTO_REPLY.FORM.INBOX.ALL_INBOXES') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <!-- Trigger Keywords (Chips / Tag List Input) -->
      <div class="w-full">
        <div class="flex items-center justify-between mb-1">
          <label class="text-sm font-medium text-slate-800 dark:text-slate-100">
            {{ $t('INSTAGRAM_AUTO_REPLY.FORM.KEYWORDS.LABEL') }}
          </label>
          <span
            v-if="keywordsList.length"
            class="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
          >
            {{ keywordsList.length }} {{ $t('INSTAGRAM_AUTO_REPLY.FORM.KEYWORDS.COUNT') }}
          </span>
        </div>

        <div
          class="flex flex-wrap items-center gap-1.5 p-2 border rounded-md min-h-[44px] border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus-within:ring-2 focus-within:ring-woot-500 focus-within:border-transparent cursor-text"
          @click="keywordInputRef?.focus()"
        >
          <!-- Chips -->
          <div
            v-for="(tag, index) in keywordsList"
            :key="index"
            class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-woot-50 text-woot-800 dark:bg-woot-900/40 dark:text-woot-200 border border-woot-200 dark:border-woot-800/60 shadow-xs"
          >
            <span>{{ tag }}</span>
            <button
              type="button"
              class="flex items-center justify-center w-3.5 h-3.5 rounded-full text-woot-600 hover:text-white hover:bg-woot-600 dark:text-woot-300 dark:hover:bg-woot-700 transition-colors cursor-pointer"
              @click.stop="removeKeyword(index)"
            >
              &times;
            </button>
          </div>

          <!-- Input for typing tags -->
          <input
            ref="keywordInputRef"
            v-model="keywordInput"
            type="text"
            class="flex-1 min-w-[140px] bg-transparent border-0 ring-0 outline-none text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 p-0 focus:ring-0 focus:border-0"
            :placeholder="
              keywordsList.length
                ? $t('INSTAGRAM_AUTO_REPLY.FORM.KEYWORDS.INPUT_MORE_PLACEHOLDER')
                : $t('INSTAGRAM_AUTO_REPLY.FORM.KEYWORDS.INPUT_PLACEHOLDER')
            "
            @keydown="handleKeywordKeydown"
            @paste="handleKeywordPaste"
            @blur="addKeyword()"
          />
        </div>
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.KEYWORDS.HELP') }}
        </p>
      </div>

      <!-- Trigger Regex (Optional) -->
      <div class="w-full">
        <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.REGEX.LABEL') }}
        </label>
        <input
          v-model="formData.trigger_regex"
          type="text"
          class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-mono text-sm"
          :placeholder="$t('INSTAGRAM_AUTO_REPLY.FORM.REGEX.PLACEHOLDER')"
        />
      </div>

      <!-- Post ID (Optional) -->
      <div class="w-full">
        <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.POST_ID.LABEL') }}
        </label>
        <input
          v-model="formData.post_id"
          type="text"
          class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-mono text-sm"
          :placeholder="$t('INSTAGRAM_AUTO_REPLY.FORM.POST_ID.PLACEHOLDER')"
        />
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.POST_ID.HELP') }}
        </p>
      </div>

      <!-- Comment Reply Text -->
      <div class="w-full">
        <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.REPLY_TEXT.LABEL') }} *
        </label>
        <textarea
          v-model="formData.reply_text"
          rows="3"
          required
          class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          :placeholder="$t('INSTAGRAM_AUTO_REPLY.FORM.REPLY_TEXT.PLACEHOLDER')"
        />
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.VARIABLES_HELP') }}
        </p>
      </div>

      <!-- DM Text (Optional) -->
      <div class="w-full">
        <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.DM_TEXT.LABEL') }}
        </label>
        <textarea
          v-model="formData.dm_text"
          rows="3"
          class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          :placeholder="$t('INSTAGRAM_AUTO_REPLY.FORM.DM_TEXT.PLACEHOLDER')"
        />
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.DM_TEXT.HELP') }}
        </p>
      </div>

      <!-- Interactive Direct Buttons (Optional) -->
      <div class="w-full p-4 border rounded-lg border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
        <div class="flex items-center justify-between mb-3">
          <div>
            <div class="text-sm font-semibold text-slate-800 dark:text-slate-100">
              🔘 {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.TITLE') }}
            </div>
            <div class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.HELP') }}
            </div>
          </div>
          <button
            v-if="buttonsList.length < 3"
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-woot-50 text-woot-700 hover:bg-woot-100 dark:bg-woot-900/30 dark:text-woot-300 dark:hover:bg-woot-900/50 border border-woot-200 dark:border-woot-800 transition-colors cursor-pointer"
            @click="addButton"
          >
            <span>+</span>
            <span>{{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.ADD_BUTTON') }}</span>
          </button>
        </div>

        <div v-if="buttonsList.length === 0" class="text-xs text-slate-400 dark:text-slate-500 italic py-1">
          {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.EMPTY') }}
        </div>

        <div v-else class="flex flex-col gap-3">
          <div
            v-for="(btn, idx) in buttonsList"
            :key="idx"
            class="p-3 border rounded-md border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-2xs"
          >
            <div class="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-slate-700/50">
              <span class="text-xs font-semibold text-slate-600 dark:text-slate-300">
                #{{ idx + 1 }}
              </span>
              <button
                type="button"
                class="text-xs text-rose-500 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 transition-colors cursor-pointer"
                @click="removeButton(idx)"
              >
                ✕
              </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <!-- Button Title -->
              <div>
                <label class="block mb-1 text-xs font-medium text-slate-700 dark:text-slate-300">
                  {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.BUTTON_TITLE_LABEL') }} *
                </label>
                <input
                  v-model="btn.title"
                  type="text"
                  required
                  class="w-full px-2.5 py-1.5 text-xs border rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  :placeholder="$t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.BUTTON_TITLE_PLACEHOLDER')"
                />
              </div>

              <!-- Button Type -->
              <div>
                <label class="block mb-1 text-xs font-medium text-slate-700 dark:text-slate-300">
                  {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.TYPE_LABEL') }}
                </label>
                <select
                  v-model="btn.type"
                  class="w-full px-2.5 py-1.5 text-xs border rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                >
                  <option value="web_url">
                    {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.TYPE_URL') }}
                  </option>
                  <option value="postback">
                    {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.TYPE_POSTBACK') }}
                  </option>
                </select>
              </div>

              <!-- URL / Payload -->
              <div class="col-span-1 md:col-span-2">
                <div v-if="btn.type === 'web_url'">
                  <label class="block mb-1 text-xs font-medium text-slate-700 dark:text-slate-300">
                    {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.URL_LABEL') }} *
                  </label>
                  <input
                    v-model="btn.url"
                    type="url"
                    required
                    class="w-full px-2.5 py-1.5 text-xs font-mono border rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    :placeholder="$t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.URL_PLACEHOLDER')"
                  />
                </div>
                <div v-else>
                  <label class="block mb-1 text-xs font-medium text-slate-700 dark:text-slate-300">
                    {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.PAYLOAD_LABEL') }}
                  </label>
                  <input
                    v-model="btn.payload"
                    type="text"
                    class="w-full px-2.5 py-1.5 text-xs border rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    :placeholder="$t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.PAYLOAD_PLACEHOLDER')"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Live Preview of Buttons -->
          <div v-if="buttonsList.some(b => b.title)" class="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
              {{ $t('INSTAGRAM_AUTO_REPLY.FORM.BUTTONS.PREVIEW_LABEL') }}
            </div>
            <div class="flex flex-col gap-1 max-w-xs">
              <div
                v-for="(b, idx) in buttonsList.filter(item => item.title)"
                :key="idx"
                class="flex items-center justify-center py-2 px-3 text-xs font-medium text-woot-600 dark:text-woot-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-2xs text-center select-none"
              >
                <span>{{ b.type === 'web_url' ? '🔗 ' : '💬 ' }} {{ b.title }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Options: Priority, Active, Default -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-200 dark:border-slate-800">
        <div>
          <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
            {{ $t('INSTAGRAM_AUTO_REPLY.FORM.PRIORITY.LABEL') }}
          </label>
          <input
            v-model.number="formData.priority"
            type="number"
            class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div class="flex items-center gap-2 pt-6">
          <input
            id="is_active_checkbox"
            v-model="formData.is_active"
            type="checkbox"
            class="w-4 h-4 text-woot-500 rounded border-slate-300 dark:border-slate-700"
          />
          <label for="is_active_checkbox" class="text-sm font-medium text-slate-800 dark:text-slate-100 cursor-pointer">
            {{ $t('INSTAGRAM_AUTO_REPLY.FORM.IS_ACTIVE.LABEL') }}
          </label>
        </div>
        <div class="flex items-center gap-2 pt-6">
          <input
            id="is_default_checkbox"
            v-model="formData.is_default"
            type="checkbox"
            class="w-4 h-4 text-woot-500 rounded border-slate-300 dark:border-slate-700"
          />
          <label for="is_default_checkbox" class="text-sm font-medium text-slate-800 dark:text-slate-100 cursor-pointer">
            {{ $t('INSTAGRAM_AUTO_REPLY.FORM.IS_DEFAULT.LABEL') }}
          </label>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end w-full gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
        <NextButton
          faded
          slate
          type="button"
          :label="$t('INSTAGRAM_AUTO_REPLY.CANCEL_BUTTON_TEXT')"
          @click="closeModal"
        />
        <NextButton
          type="submit"
          :is-loading="loading"
          :label="
            isEditing
              ? $t('INSTAGRAM_AUTO_REPLY.EDIT.BUTTON_TEXT')
              : $t('INSTAGRAM_AUTO_REPLY.ADD.BUTTON_TEXT')
          "
        />
      </div>
    </form>
  </div>
</template>
